import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const {
  FiGlobe,
  FiEye,
  FiEyeOff,
  FiSave,
  FiCheckCircle,
  FiAlertCircle,
  FiCopy,
  FiExternalLink,
  FiInfo
} = FiIcons;

function PublicResumeSettings() {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    username: '',
    isPublic: false
  });

  const [usernameError, setUsernameError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const baseUrl = window.location.origin;

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();

      const { data, error } = await supabase
        .from('profiles')
        .select('username, is_public')
        .eq('user_id', activeUserId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setFormData({
          username: data.username || '',
          isPublic: data.is_public || false
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const validateUsername = (username) => {
    if (!username) {
      return 'Username is required';
    }

    if (username.length < 3) {
      return 'Username must be at least 3 characters';
    }

    if (username.length > 30) {
      return 'Username must be less than 30 characters';
    }

    // Only lowercase letters, numbers, hyphens, and underscores
    if (!/^[a-z0-9_-]+$/.test(username)) {
      return 'Username can only contain lowercase letters, numbers, hyphens, and underscores';
    }

    // Cannot start or end with hyphen or underscore
    if (/^[-_]|[-_]$/.test(username)) {
      return 'Username cannot start or end with a hyphen or underscore';
    }

    // Reserved usernames
    const reserved = ['admin', 'api', 'www', 'app', 'dashboard', 'settings', 'profile', 'user', 'athlete'];
    if (reserved.includes(username.toLowerCase())) {
      return 'This username is reserved';
    }

    return null;
  };

  const checkUsernameAvailability = async (username) => {
    if (!username) {
      setUsernameAvailable(null);
      return;
    }

    const validationError = validateUsername(username);
    if (validationError) {
      setUsernameError(validationError);
      setUsernameAvailable(null);
      return;
    }

    setUsernameError('');
    setCheckingUsername(true);

    try {
      const activeUserId = getActiveUserId();

      // Check if username exists (excluding current user)
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username.toLowerCase())
        .neq('user_id', activeUserId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setUsernameAvailable(false);
        setUsernameError('This username is already taken');
      } else {
        setUsernameAvailable(true);
        setUsernameError('');
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameError('Failed to check username availability');
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (value) => {
    const lowercase = value.toLowerCase();
    setFormData(prev => ({ ...prev, username: lowercase }));

    // Clear previous validation
    setUsernameError('');
    setUsernameAvailable(null);

    // Debounce username availability check
    if (usernameCheckTimeout) {
      clearTimeout(usernameCheckTimeout);
    }

    usernameCheckTimeout = setTimeout(() => {
      checkUsernameAvailability(lowercase);
    }, 500);
  };

  let usernameCheckTimeout;

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Validate username
      const validationError = validateUsername(formData.username);
      if (validationError) {
        setUsernameError(validationError);
        return;
      }

      // Check availability one more time before saving
      if (usernameAvailable === false) {
        setUsernameError('This username is already taken');
        return;
      }

      const activeUserId = getActiveUserId();

      const { error } = await supabase
        .from('profiles')
        .update({
          username: formData.username.toLowerCase(),
          is_public: formData.isPublic,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', activeUserId);

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          setUsernameError('This username is already taken');
          return;
        }
        throw error;
      }

      showMessage('success', 'Public resume settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const copyPublicUrl = () => {
    const url = `${baseUrl}/${formData.username}`;
    navigator.clipboard.writeText(url);
    showMessage('success', 'Public URL copied to clipboard!');
  };

  const openPublicResume = () => {
    const url = `${baseUrl}/${formData.username}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Message */}
      <AnimatePresence>
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`rounded-lg p-4 flex items-start space-x-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <SafeIcon
              icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle}
              className={`w-5 h-5 mt-0.5 ${
                message.type === 'success' ? 'text-green-600' : 'text-red-600'
              }`}
            />
            <p
              className={`text-sm ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}
            >
              {message.text}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Public Resume Settings */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-start space-x-4 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-space-grotesk font-semibold text-secondary mb-2">
              Public Resume
            </h3>
            <p className="text-gray-600 text-sm">
              Create a shareable public resume that college coaches can view
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Username Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Username *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="your-username"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10 ${
                  usernameError
                    ? 'border-red-300'
                    : usernameAvailable === true
                    ? 'border-green-300'
                    : 'border-gray-200'
                }`}
              />
              {checkingUsername && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {!checkingUsername && usernameAvailable === true && (
                <SafeIcon
                  icon={FiCheckCircle}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500"
                />
              )}
              {!checkingUsername && (usernameError || usernameAvailable === false) && (
                <SafeIcon
                  icon={FiAlertCircle}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500"
                />
              )}
            </div>
            {usernameError && (
              <p className="text-xs text-red-600 mt-1">{usernameError}</p>
            )}
            {!usernameError && usernameAvailable === true && (
              <p className="text-xs text-green-600 mt-1">Username is available!</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Only lowercase letters, numbers, hyphens, and underscores (3-30 characters)
            </p>
          </div>

          {/* Public URL Preview */}
          {formData.username && !usernameError && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-2">Your Public URL:</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-white px-3 py-2 rounded text-sm text-primary border border-blue-200 overflow-x-auto">
                  {baseUrl}/{formData.username}
                </code>
                <button
                  onClick={copyPublicUrl}
                  className="p-2 bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  title="Copy URL"
                >
                  <SafeIcon icon={FiCopy} className="w-4 h-4 text-primary" />
                </button>
                {formData.isPublic && (
                  <button
                    onClick={openPublicResume}
                    className="p-2 bg-primary text-white rounded hover:bg-blue-700 transition-colors"
                    title="Open in new tab"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Public Toggle */}
          <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-semibold text-gray-800">Make Resume Public</h4>
                <SafeIcon
                  icon={formData.isPublic ? FiEye : FiEyeOff}
                  className={`w-4 h-4 ${formData.isPublic ? 'text-green-600' : 'text-gray-400'}`}
                />
              </div>
              <p className="text-sm text-gray-600">
                {formData.isPublic
                  ? 'Your resume is public and can be viewed by anyone with the link'
                  : 'Your resume is private and only visible to you'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer ml-4">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiInfo} className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1 text-sm">Important Information</h4>
                <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                  <li>When public, your entire profile (name, contact info, stats, academic records, etc.) will be visible</li>
                  <li>College coaches and recruiters can view your public resume without signing in</li>
                  <li>You can turn off public access at any time</li>
                  <li>Your username must be unique and cannot be changed once other users start using the platform</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving || !!usernameError || checkingUsername || !formData.username}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default PublicResumeSettings;